import { useEffect, useState } from 'react';

import { Modal, useToast } from '../ui/Overlay';
import { Button } from '../ui/Button';
import { Field, Input, Select } from '../ui/Field';
import { useMutation } from '../../hooks/useResource';
import { useAuth } from '../../auth/AuthProvider';
import { api } from '../../lib/api';

/**
 * Organisation settings.
 *
 * The same dialog serves a platform administrator editing any tenant and an
 * organisation administrator editing their own, because they are the same
 * operation with a different scope — and the one field that is not the same,
 * suspending a tenant, is hidden and also refused server-side. Two dialogs
 * would mean two chances to get that boundary wrong.
 *
 * Suspending is not deletion. A suspended organisation keeps its data and its
 * history; its people simply cannot sign in. Telematics data outlives the
 * commercial relationship that produced it, which is why nothing here deletes.
 */
export function OrgSettingsModal({ open, organization, onClose, onSaved }) {
  const toast = useToast();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [status, setStatus] = useState('ACTIVE');

  useEffect(() => {
    setName(organization?.name || '');
    setStatus(organization?.status || 'ACTIVE');
  }, [organization]);

  // Only a platform administrator may change a tenant's status; the server
  // answers `forbidden_field` to anyone else, so the field is not offered.
  const canSuspend = user?.role === 'SUPER_ADMIN';

  const save = useMutation(async () =>
    api.patch(`/api/orgs/${organization.id}`, {
      name: name.trim(),
      status: canSuspend ? status : undefined,
    })
  );

  const submit = async () => {
    try {
      await save.run();
      toast.success('Organisation updated.');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!organization) return null;

  const unchanged = name === organization.name && status === organization.status;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Organisation settings"
      description={`${organization.slug || organization.name} · created ${new Date(organization.created_at).toLocaleDateString()}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.pending}
            onClick={submit}
            disabled={!name.trim() || unchanged}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="Organisation name"
          required
          hint="Shown to everyone in this organisation, and on the platform list."
        >
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        {canSuspend && (
          <Field
            label="Status"
            hint="Suspending stops everyone here signing in. Nothing is deleted and devices keep reporting."
          >
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'SUSPENDED', label: 'Suspended' },
              ]}
            />
          </Field>
        )}

        {canSuspend && status === 'SUSPENDED' && organization.status !== 'SUSPENDED' && (
          <p className="rounded-lg border border-critical/30 bg-critical-soft px-3 py-2 text-[12.5px] text-critical-ink">
            {organization.user_count ?? 'Its'} account
            {Number(organization.user_count) === 1 ? '' : 's'} will be signed out and refused at the
            next sign-in. Devices keep reporting and the data keeps accruing.
          </p>
        )}
      </div>
    </Modal>
  );
}
